// ИИ → Составить план: цель словами, тренировок в неделю, время, оборудование, ограничения.
// ИИ возвращает черновик (шаблоны и расписание); упражнения — только из справочника. Применяется
// только по кнопке и попадает в журнал с источником «ИИ».

import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { errorMessage } from '@/api/client'
import { Badge, Button, Card, ErrorText, PageSpinner, Select, Spinner, controlClass, useToast } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/format'

import {
  useAiStatus,
  useApplyProposal,
  usePropose,
  useProposals,
  useRejectProposal,
  useResolveProposal,
  type Equipment,
  type Proposal,
  type ProposalIssue,
} from './api'
import {
  EQUIPMENT_LABELS,
  PROPOSAL_STATUS,
  canApply,
  cardioText,
  exerciseTargets,
  matchNote,
  weekdayLabel,
} from './logic'
import { Disclaimer, Markdown, NotConfigured } from './parts'

const SESSIONS = [1, 2, 3, 4, 5, 6, 7].map((n) => ({ value: String(n), label: String(n) }))
const MINUTES = [30, 45, 60, 75, 90, 120].map((n) => ({ value: String(n), label: `${n} мин` }))

function PlanForm({ onCreated, disabled }: { onCreated: (p: Proposal) => void; disabled: boolean }) {
  const propose = usePropose()
  const [goal, setGoal] = useState('')
  const [sessions, setSessions] = useState('3')
  const [minutes, setMinutes] = useState('60')
  const [equipment, setEquipment] = useState<Equipment[]>([])
  const [constraints, setConstraints] = useState('')

  const toggle = (key: Equipment) =>
    setEquipment((list) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]))

  const submit = (event: FormEvent) => {
    event.preventDefault()
    propose.mutate(
      {
        goal: goal.trim(),
        sessions_per_week: Number(sessions),
        minutes_per_session: Number(minutes),
        equipment,
        constraints: constraints.trim() || null,
      },
      { onSuccess: onCreated },
    )
  }

  return (
    <Card title="Что нужно" subtitle="ИИ учтёт активные травмы и цели. Черновик ничего не меняет, пока ты его не применишь.">
      <form className="grid gap-4" onSubmit={submit}>
        <div className="grid gap-1.5">
          <label htmlFor="plan-goal" className="text-sm font-medium text-fg">
            Цель
          </label>
          <textarea
            id="plan-goal"
            rows={2}
            maxLength={1000}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="Например: стать сильнее в базовых упражнениях и сбросить 3 кг"
            className={cn(controlClass, 'h-auto min-h-16 resize-y py-2')}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select label="Тренировок в неделю" value={sessions} onChange={(e) => setSessions(e.target.value)} options={SESSIONS} />
          <Select label="Время на тренировку" value={minutes} onChange={(e) => setMinutes(e.target.value)} options={MINUTES} />
        </div>
        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium text-fg">Оборудование</legend>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(EQUIPMENT_LABELS) as Equipment[]).map((key) => (
              <Button
                key={key}
                type="button"
                size="sm"
                variant={equipment.includes(key) ? 'primary' : 'secondary'}
                aria-pressed={equipment.includes(key)}
                onClick={() => toggle(key)}
              >
                {EQUIPMENT_LABELS[key]}
              </Button>
            ))}
          </div>
          <p className="text-sm text-muted">Ничего не выбрано — подойдёт любое.</p>
        </fieldset>
        <div className="grid gap-1.5">
          <label htmlFor="plan-constraints" className="text-sm font-medium text-fg">
            Ограничения
          </label>
          <textarea
            id="plan-constraints"
            rows={2}
            maxLength={1000}
            value={constraints}
            onChange={(e) => setConstraints(e.target.value)}
            placeholder="Например: без прыжков, не больше 3 упражнений на ноги"
            className={cn(controlClass, 'h-auto min-h-16 resize-y py-2')}
          />
        </div>
        {propose.isError ? <ErrorText error={propose.error} /> : null}
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" variant="primary" icon="ai" loading={propose.isPending} disabled={disabled || goal.trim().length < 3}>
            Составить план
          </Button>
          {propose.isPending ? <span className="text-sm text-muted">ИИ составляет план — это может занять до минуты.</span> : null}
        </div>
      </form>
    </Card>
  )
}

function IssueRow({ issue, proposal, onChanged }: { issue: ProposalIssue; proposal: Proposal; onChanged: (p: Proposal) => void }) {
  const resolve = useResolveProposal()
  const toast = useToast()
  if (issue.kind !== 'unknown_exercise' || issue.template_index === null || issue.exercise_index === null) {
    return <ErrorText>{issue.message}</ErrorText>
  }
  const fix = (action: 'replace' | 'remove', exerciseId?: string) =>
    resolve.mutate(
      {
        id: proposal.id,
        fix: {
          template_index: issue.template_index!,
          exercise_index: issue.exercise_index!,
          action,
          ...(exerciseId ? { exercise_id: exerciseId } : {}),
        },
      },
      { onSuccess: onChanged, onError: (err) => toast.show({ message: errorMessage(err), tone: 'danger' }) },
    )
  return (
    <li className="grid gap-2 rounded-lg border border-warning/40 bg-warning-soft/40 p-3">
      <p className="text-sm text-fg">{issue.message}</p>
      <div className="flex flex-wrap gap-2">
        {issue.suggestions.map((s) => (
          <Button key={s.id} size="sm" disabled={resolve.isPending} onClick={() => fix('replace', s.id)}>
            {`Заменить на «${s.name}»`}
          </Button>
        ))}
        <Button size="sm" variant="ghost" disabled={resolve.isPending} onClick={() => fix('remove')}>
          Убрать
        </Button>
        {resolve.isPending ? <Spinner size={16} /> : null}
      </div>
    </li>
  )
}

function ProposalView({ proposal, onChanged }: { proposal: Proposal; onChanged: (p: Proposal) => void }) {
  const apply = useApplyProposal()
  const reject = useRejectProposal()
  const toast = useToast()
  const body = proposal.proposal
  const status = PROPOSAL_STATUS[proposal.status]
  const unknown = proposal.issues.filter((i) => i.kind === 'unknown_exercise')
  const schema = proposal.issues.filter((i) => i.kind !== 'unknown_exercise')

  return (
    <Card
      title="Черновик плана"
      subtitle={`${formatDateTime(proposal.created_at)} · цель: ${proposal.input.goal}`}
      actions={<Badge tone={status.tone}>{status.text}</Badge>}
    >
      <div className="grid gap-4">
        {schema.length ? (
          <div className="grid gap-2">
            {schema.map((issue, i) => (
              <ErrorText key={i}>{issue.message}</ErrorText>
            ))}
            <p className="text-sm text-muted">План не применён. Составь его заново — можно уточнить цель.</p>
          </div>
        ) : null}
        {unknown.length ? (
          <ul className="grid gap-2">
            {unknown.map((issue) => (
              <IssueRow key={`${issue.template_index}-${issue.exercise_index}`} issue={issue} proposal={proposal} onChanged={onChanged} />
            ))}
          </ul>
        ) : null}

        {body ? (
          <>
            {body.summary ? <Markdown text={body.summary} /> : null}
            <div className="grid gap-3 md:grid-cols-2">
              {body.templates.map((t) => (
                <section key={t.key} className="rounded-lg border border-border p-3">
                  <h3 className="text-sm font-semibold text-fg">{t.name}</h3>
                  <ol className="mt-2 grid gap-1.5 text-sm">
                    {t.exercises.map((ex, i) => (
                      <li key={i} className="flex flex-wrap items-baseline justify-between gap-x-3">
                        <span className={cn(ex.exercise_id ? 'text-fg' : 'text-warning')}>
                          {ex.exercise_name ?? ex.name}
                          {ex.exercise_id ? null : ' (нет в справочнике)'}
                          {matchNote(ex) ? <span className="block text-xs text-muted">{matchNote(ex)}</span> : null}
                        </span>
                        <span className="tabular-nums text-muted">{exerciseTargets(ex)}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
            <section>
              <h3 className="text-sm font-semibold text-fg">Расписание</h3>
              <ul className="mt-2 grid gap-1 text-sm">
                {body.schedule.map((r, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="w-28 shrink-0 text-muted">{weekdayLabel(r.weekday)}</span>
                    <span className="text-fg">
                      {r.kind === 'strength' ? (r.template_name ?? r.template) : r.cardio ? cardioText(r.cardio) : 'Кардио'}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          </>
        ) : null}

        {proposal.status === 'pending' && body ? (
          <div className="grid gap-2 border-t border-border pt-3">
            <p className="text-sm text-muted">
              Применение создаст шаблоны и заменит текущее расписание этим. Всё попадёт в журнал — откатить можно там.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                icon="check"
                disabled={!canApply(proposal)}
                loading={apply.isPending}
                onClick={() =>
                  apply.mutate(proposal.id, {
                    onSuccess: (p) => {
                      onChanged(p)
                      toast.show({ message: 'План применён: шаблоны и расписание созданы', tone: 'accent' })
                    },
                    onError: (err) => toast.show({ message: errorMessage(err), tone: 'danger' }),
                  })
                }
              >
                Применить
              </Button>
              <Button
                variant="ghost"
                loading={reject.isPending}
                onClick={() => reject.mutate(proposal.id, { onSuccess: onChanged })}
              >
                Отклонить
              </Button>
            </div>
          </div>
        ) : null}
        {proposal.status === 'applied' ? (
          <p className="text-sm text-muted">
            Готово: смотри{' '}
            <Link to="/plan/rules" className="text-accent hover:underline">
              расписание
            </Link>{' '}
            и{' '}
            <Link to="/templates" className="text-accent hover:underline">
              шаблоны
            </Link>
            .
          </p>
        ) : null}
        <Disclaimer />
      </div>
    </Card>
  )
}

export function PlanPage() {
  const status = useAiStatus()
  const proposals = useProposals()
  const [shown, setShown] = useState<Proposal | null>(null)
  const configured = status.data?.configured === true

  const list = proposals.data ?? []
  const current = (shown && (list.find((p) => p.id === shown.id && p.updated_at >= shown.updated_at) ?? shown)) ?? list[0] ?? null
  const others = list.filter((p) => p.id !== current?.id)

  return (
    <div className="grid max-w-4xl gap-4">
      {status.data && !configured ? (
        <Card>
          <NotConfigured />
        </Card>
      ) : null}
      <PlanForm onCreated={setShown} disabled={!configured} />
      {proposals.isPending ? <PageSpinner /> : proposals.isError ? <ErrorText error={proposals.error} /> : null}
      {current ? <ProposalView key={current.id} proposal={current} onChanged={setShown} /> : null}
      {others.length ? (
        <Card title="Прошлые черновики" flush>
          <ul className="pb-2">
            {others.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setShown(p)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm hover:bg-surface-2"
                >
                  <span className="min-w-0 truncate text-fg">
                    {formatDateTime(p.created_at)} · {p.input.goal}
                  </span>
                  <span className="shrink-0 text-xs text-muted">{PROPOSAL_STATUS[p.status].text}</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  )
}
